import React, { useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { Select, Value } from 'baseui/select';
import { Notification, KIND } from 'baseui/notification';
import { gameplayGuardrailApi } from './api';
import ChatHistoryView from './ChatHistoryView';
import { useGuardrailCategories } from './guardrailCategories';

type WindowMode = 'LAST_N_DAYS' | 'TIMESTAMP_RANGE';

interface ModeOption {
	id: WindowMode;
	label: string;
}

const WINDOW_OPTIONS: ModeOption[] = [
	{ id: 'LAST_N_DAYS', label: 'Last N Days' },
	{ id: 'TIMESTAMP_RANGE', label: 'Custom Range' }
];

interface SimpleOption {
	id: string;
	label: string;
}

const SEVERITY_OPTIONS: SimpleOption[] = [
	{ id: '', label: 'All severities' },
	{ id: 'LOW', label: 'LOW' },
	{ id: 'MED', label: 'MED' },
	{ id: 'HIGH', label: 'HIGH' },
	{ id: 'CRIT', label: 'CRIT' }
];

// Queue is now sourced from guardrail_actions, so every row already has
// at least one enforcement action. This filter narrows on the violations
// side: rows with / without matching guardrails_user_data entries.
const HAS_ACTION_OPTIONS: SimpleOption[] = [
	{ id: 'all', label: 'All' },
	{ id: 'true', label: 'With violations' },
	{ id: 'false', label: 'Action without violations' }
];

// Narrows the queue to pairs that received one of these enforcement actions
// in the window. Values are the lowercase GuardrailActionType strings cognilens
// stores; the backend matches them case-insensitively.
const ACTION_TYPE_OPTIONS: { id: string; label: string; types: string[] }[] = [
	{ id: 'all', label: 'All action types', types: [] },
	{ id: 'any_block', label: 'Any block (soft or hard)', types: ['soft_blocked', 'hard_blocked', 'blocked'] },
	{ id: 'soft_blocked', label: 'Soft blocked', types: ['soft_blocked'] },
	{ id: 'hard_blocked', label: 'Hard blocked', types: ['hard_blocked'] },
	{ id: 'warned', label: 'Warned', types: ['warned'] },
	{ id: 'flagged_for_review', label: 'Flagged for review', types: ['flagged_for_review'] }
];

// cognilens stores the classifier vocabulary (MEDIUM / CRITICAL); the
// dashboard abbreviates. Fold both spellings into one bucket for display.
const SEVERITY_DISPLAY_KEY: Record<string, string> = {
	LOW: 'LOW',
	MED: 'MED',
	MEDIUM: 'MED',
	HIGH: 'HIGH',
	CRIT: 'CRIT',
	CRITICAL: 'CRIT'
};

const normaliseSeverityCounts = (counts?: Record<string, number>): Record<string, number> => {
	const out: Record<string, number> = {};
	Object.entries(counts || {}).forEach(([k, v]) => {
		const key = SEVERITY_DISPLAY_KEY[k.toUpperCase()] || k.toUpperCase();
		out[key] = (out[key] || 0) + v;
	});
	return out;
};

const labelStyle = {
	fontSize: '13px',
	fontWeight: '500' as const,
	color: '#374151',
	display: 'block',
	marginBottom: '6px'
};

const cardStyle = {
	backgroundColor: '#fff',
	border: '1px solid #e5e7eb',
	borderRadius: '12px',
	padding: '24px',
	marginBottom: '20px'
};

interface NotificationState {
	message: string;
	kind: typeof KIND[keyof typeof KIND];
}

interface QueueItem {
	userId: string;
	botId: string;
	violationCount: number;
	pendingViolationCount?: number;
	actionCount: number;
	topCategory?: string;
	categories?: string[];
	severityCounts?: Record<string, number>;
	nullSeverityCount?: number;
	lastActionType?: string | null;
	hasBlock?: boolean;
	hasFlaggedForReview?: boolean;
	lastTimestamp?: number;
	engines?: string[];
}

const datetimeLocalToEpochMs = (s: string): number | null => {
	if (!s) return null;
	const t = new Date(s).getTime();
	return Number.isNaN(t) ? null : t;
};

const formatTimestamp = (raw: unknown): string => {
	const n = typeof raw === 'number' ? raw : Number(raw);
	if (!n || Number.isNaN(n)) return String(raw ?? '');
	return new Date(n).toLocaleString();
};

const ENGINE_COLORS: Record<string, { bg: string; fg: string }> = {
	ENCODER: { bg: '#ede9fe', fg: '#5b21b6' },
	LLM: { bg: '#e0f2fe', fg: '#075985' }
};

const EngineBadges: React.FC<{ engines?: string[]; css: ReturnType<typeof useStyletron>[0] }> = ({ engines, css }) => {
	const known = (engines || []).filter((e) => !!e);
	if (known.length === 0) return <span className={css({ color: '#9ca3af' })}>—</span>;
	return (
		<span className={css({ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' })}>
			{known.map((engine) => {
				const colors = ENGINE_COLORS[engine] || { bg: '#f3f4f6', fg: '#374151' };
				return (
					<span
						key={engine}
						className={css({
							backgroundColor: colors.bg,
							color: colors.fg,
							borderRadius: '4px',
							padding: '2px 6px',
							fontSize: '11px',
							fontWeight: '600'
						})}>
						{engine}
					</span>
				);
			})}
		</span>
	);
};

const StatusBadges: React.FC<{ item: QueueItem; css: ReturnType<typeof useStyletron>[0] }> = ({ item, css }) => {
	const badges: { label: string; bg: string; fg: string }[] = [];
	if (item.hasBlock) badges.push({ label: '🔴 blocked', bg: '#fee2e2', fg: '#991b1b' });
	if (item.hasFlaggedForReview) badges.push({ label: '🟡 flagged', bg: '#fef3c7', fg: '#92400e' });
	if (!item.hasBlock && !item.hasFlaggedForReview) {
		if (item.actionCount > 0) {
			badges.push({ label: '✓ warned', bg: '#dbeafe', fg: '#1e40af' });
		} else if (item.violationCount > 0) {
			badges.push({ label: '⚪ no action', bg: '#f3f4f6', fg: '#374151' });
		}
	}
	if ((item.nullSeverityCount ?? 0) > 0) {
		badges.push({ label: '⚠ null sev', bg: '#fde68a', fg: '#78350f' });
	}
	// Only reachable through a user ID lookup: the queue drops fully-reviewed
	// pairs unless a userId filter is set.
	if (item.violationCount > 0 && item.pendingViolationCount === 0) {
		badges.push({ label: '✓ reviewed', bg: '#d1fae5', fg: '#065f46' });
	}
	if (badges.length === 0) {
		return <span className={css({ fontSize: '12px', color: '#9ca3af' })}>—</span>;
	}
	return (
		<div className={css({ display: 'flex', flexWrap: 'wrap', gap: '4px' })}>
			{badges.map((b) => (
				<span
					key={b.label}
					className={css({
						fontSize: '11px',
						fontWeight: '600',
						padding: '2px 6px',
						borderRadius: '4px',
						backgroundColor: b.bg,
						color: b.fg,
						whiteSpace: 'nowrap'
					})}>
					{b.label}
				</span>
			))}
		</div>
	);
};

const SeverityMix: React.FC<{ counts?: Record<string, number>; css: ReturnType<typeof useStyletron>[0] }> = ({ counts, css }) => {
	const order = ['CRIT', 'HIGH', 'MED', 'LOW'];
	const colors: Record<string, string> = {
		CRIT: '#991b1b',
		HIGH: '#b45309',
		MED: '#1d4ed8',
		LOW: '#4b5563'
	};
	const normalised = normaliseSeverityCounts(counts);
	const entries = order.filter((k) => normalised[k]);
	if (!entries.length) return <span className={css({ fontSize: '12px', color: '#9ca3af' })}>—</span>;
	return (
		<div className={css({ display: 'flex', gap: '6px', flexWrap: 'wrap' })}>
			{entries.map((k) => (
				<span key={k} className={css({ fontSize: '12px', color: colors[k], fontWeight: '600' })}>
					{k} {normalised[k]}
				</span>
			))}
		</div>
	);
};

const GameplayGuardrailReviewPanel: React.FC = () => {
	const [css] = useStyletron();
	const categoryOptions = useGuardrailCategories();
	const [windowMode, setWindowMode] = useState<Value>([WINDOW_OPTIONS[0]]);
	const mode = (windowMode[0]?.id as WindowMode) || 'LAST_N_DAYS';

	const [days, setDays] = useState('7');
	const [startTs, setStartTs] = useState('');
	const [endTs, setEndTs] = useState('');
	const [categoryValue, setCategoryValue] = useState<Value>([]);
	const category = (categoryValue[0]?.id as string) || '';
	const [severityValue, setSeverityValue] = useState<Value>([SEVERITY_OPTIONS[0]]);
	const [hasActionValue, setHasActionValue] = useState<Value>([HAS_ACTION_OPTIONS[0]]);
	const [actionTypeValue, setActionTypeValue] = useState<Value>([ACTION_TYPE_OPTIONS[0]]);
	const [userIdFilter, setUserIdFilter] = useState('');
	const [pageSize, setPageSize] = useState('50');
	const [page, setPage] = useState(1);

	const [items, setItems] = useState<QueueItem[]>([]);
	const [total, setTotal] = useState(0);
	const [counters, setCounters] = useState<{ pending: number; doneToday: number } | null>(null);
	const [loading, setLoading] = useState(false);
	const [notification, setNotification] = useState<NotificationState | null>(null);
	const [hasSearched, setHasSearched] = useState(false);
	const [selectedChat, setSelectedChat] = useState<{ userId: string; botId: string } | null>(null);

	const buildWindow = (): { window: { startTs?: number; endTs?: number; days?: number }; error?: string } => {
		if (mode === 'LAST_N_DAYS') {
			const n = parseInt(days, 10);
			if (!n || n < 1 || n > 365) return { window: {}, error: 'Days must be between 1 and 365' };
			return { window: { days: n } };
		}
		const s = datetimeLocalToEpochMs(startTs);
		const e = datetimeLocalToEpochMs(endTs);
		if (s === null || e === null) return { window: {}, error: 'Both start and end timestamps are required' };
		if (s > e) return { window: {}, error: 'Start must be before end' };
		return { window: { startTs: s, endTs: e } };
	};

	const fetchCounters = async (window: { startTs?: number; endTs?: number; days?: number }) => {
		const result = await gameplayGuardrailApi.getReviewCounters(window);
		if (result.success && result.data) {
			setCounters(result.data);
		}
	};

	const runSearch = async (targetPage: number) => {
		const built = buildWindow();
		if (built.error) {
			setNotification({ message: built.error, kind: KIND.negative });
			return;
		}
		const ps = Math.max(1, Math.min(200, parseInt(pageSize, 10) || 50));
		const sev = (severityValue[0]?.id as string) || '';
		const hasActionId = (hasActionValue[0]?.id as string) || 'all';
		const hasAction = hasActionId === 'all' ? undefined : hasActionId === 'true';
		const actionTypeId = (actionTypeValue[0]?.id as string) || 'all';
		const actionTypes = ACTION_TYPE_OPTIONS.find((o) => o.id === actionTypeId)?.types || [];
		const userId = userIdFilter.trim();
		if (userId && !/^\d+$/.test(userId)) {
			setNotification({ message: 'Zupee user ID must be numeric', kind: KIND.negative });
			return;
		}

		setLoading(true);
		setNotification(null);
		try {
			const [queue] = await Promise.all([
				gameplayGuardrailApi.getReviewQueue({
					...built.window,
					userId: userId || undefined,
					category: category.trim() || undefined,
					severity: (sev || undefined) as 'LOW' | 'MED' | 'HIGH' | 'CRIT' | undefined,
					actionTypes: actionTypes.length > 0 ? actionTypes : undefined,
					hasAction,
					page: targetPage,
					pageSize: ps
				}),
				fetchCounters(built.window)
			]);
			if (queue.success && queue.data) {
				setItems(queue.data.items || []);
				setTotal(queue.data.total || 0);
				setPage(queue.data.page || targetPage);
				setHasSearched(true);
				if ((queue.data.items || []).length === 0) {
					setNotification({ message: 'No pairs matched this filter', kind: KIND.warning });
				}
			} else {
				setNotification({ message: queue.error || 'Search failed', kind: KIND.negative });
			}
		} catch (e) {
			setNotification({ message: 'Network error. Please try again.', kind: KIND.negative });
		}
		setLoading(false);
	};

	const handleSearch = () => {
		setPage(1);
		runSearch(1);
	};

	useEffect(() => {
		// Load counters once on mount with default window (last 7 days).
		fetchCounters({ days: 7 });
	}, []);

	const ps = Math.max(1, Math.min(200, parseInt(pageSize, 10) || 50));
	const totalPages = total > 0 ? Math.max(1, Math.ceil(total / ps)) : 0;
	const canPrev = page > 1 && !loading;
	const canNext = page < totalPages && !loading;

	const renderWindowFields = () => {
		if (mode === 'LAST_N_DAYS') {
			return (
				<div className={css({ maxWidth: '180px' })}>
					<label className={css(labelStyle)}>Days (1–365) *</label>
					<Input value={days} onChange={(e) => setDays(e.currentTarget.value)} type='number' />
				</div>
			);
		}
		return (
			<div className={css({ display: 'flex', gap: '12px', flexWrap: 'wrap' })}>
				<div className={css({ flex: '1 1 240px' })}>
					<label className={css(labelStyle)}>Start *</label>
					<input
						type='datetime-local'
						value={startTs}
						onChange={(e) => setStartTs(e.target.value)}
						className={css({
							width: '100%',
							padding: '10px 12px',
							fontSize: '14px',
							border: '1px solid #cbd5e1',
							borderRadius: '6px',
							backgroundColor: '#fff'
						})}
					/>
				</div>
				<div className={css({ flex: '1 1 240px' })}>
					<label className={css(labelStyle)}>End *</label>
					<input
						type='datetime-local'
						value={endTs}
						onChange={(e) => setEndTs(e.target.value)}
						className={css({
							width: '100%',
							padding: '10px 12px',
							fontSize: '14px',
							border: '1px solid #cbd5e1',
							borderRadius: '6px',
							backgroundColor: '#fff'
						})}
					/>
				</div>
			</div>
		);
	};

	const cellStyle = {
		padding: '10px 12px',
		fontSize: '13px',
		color: '#374151',
		borderBottom: '1px solid #f1f5f9',
		verticalAlign: 'top' as const,
		whiteSpace: 'nowrap' as const
	};
	const headerCellStyle = {
		...cellStyle,
		fontWeight: '600' as const,
		color: '#111827',
		backgroundColor: '#f9fafb',
		borderBottom: '1px solid #e5e7eb',
		position: 'sticky' as const,
		top: 0
	};

	if (selectedChat) {
		return (
			<div className={css({ height: '100%', display: 'flex', flexDirection: 'column' })}>
				<ChatHistoryView
					userId={selectedChat.userId}
					botId={selectedChat.botId}
					onBack={() => {
						setSelectedChat(null);
						// Refresh queue + counters since the pair may now have a verdict.
						const built = buildWindow();
						if (!built.error) {
							runSearch(1);
						}
					}}
				/>
			</div>
		);
	}

	return (
		<div>
			<h3 className={css({ fontSize: '20px', fontWeight: '600', color: '#111', marginBottom: '6px' })}>Guardrail Review</h3>
			<p className={css({ fontSize: '14px', color: '#6b7280', marginBottom: '16px' })}>
				Review the moderation system&apos;s decisions. Each row is one (user, bot) pair with at least one enforcement action;
				click a row to read the chat and submit a verdict for each violation — pairs whose violations are all reviewed drop
				off the queue.
			</p>

			{/* Counter strip */}
			<div className={css({ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' })}>
				<div
					className={css({
						backgroundColor: '#eff6ff',
						border: '1px solid #bfdbfe',
						borderRadius: '8px',
						padding: '12px 18px',
						minWidth: '160px'
					})}>
					<div className={css({ fontSize: '11px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' })}>
						Pending
					</div>
					<div className={css({ fontSize: '22px', fontWeight: '700', color: '#1e3a8a' })}>
						{counters ? counters.pending : '—'}
					</div>
				</div>
				<div
					className={css({
						backgroundColor: '#ecfdf5',
						border: '1px solid #a7f3d0',
						borderRadius: '8px',
						padding: '12px 18px',
						minWidth: '160px'
					})}>
					<div className={css({ fontSize: '11px', color: '#065f46', fontWeight: '600', textTransform: 'uppercase' })}>
						Done today
					</div>
					<div className={css({ fontSize: '22px', fontWeight: '700', color: '#064e3b' })}>
						{counters ? counters.doneToday : '—'}
					</div>
				</div>
			</div>

			{/* Filter card */}
			<div className={css(cardStyle)}>
				<div className={css({ display: 'flex', flexDirection: 'column', gap: '16px' })}>
					<div className={css({ display: 'flex', gap: '12px', flexWrap: 'wrap' })}>
						<div className={css({ minWidth: '200px' })}>
							<label className={css(labelStyle)}>Window</label>
							<Select
								options={WINDOW_OPTIONS}
								labelKey='label'
								valueKey='id'
								clearable={false}
								searchable={false}
								value={windowMode}
								onChange={({ value }) => {
									setWindowMode(value);
									setNotification(null);
								}}
							/>
						</div>
						<div className={css({ flex: '1 1 240px' })}>{renderWindowFields()}</div>
					</div>

					<div className={css({ display: 'flex', gap: '12px', flexWrap: 'wrap' })}>
						<div className={css({ minWidth: '240px' })}>
							<label className={css(labelStyle)}>Zupee user ID</label>
							<Input
								value={userIdFilter}
								onChange={(e) => setUserIdFilter(e.currentTarget.value)}
								placeholder='All users'
								clearable
							/>
							<div className={css({ fontSize: '11px', color: '#6b7280', marginTop: '4px' })}>
								The window above still applies. Reviewed pairs are included for a user lookup.
							</div>
						</div>
						<div className={css({ minWidth: '220px' })}>
							<label className={css(labelStyle)}>Action type</label>
							<Select
								options={ACTION_TYPE_OPTIONS}
								labelKey='label'
								valueKey='id'
								clearable={false}
								searchable={false}
								value={actionTypeValue}
								onChange={({ value }) => setActionTypeValue(value)}
							/>
						</div>
					</div>

					<div className={css({ display: 'flex', gap: '12px', flexWrap: 'wrap' })}>
						<div className={css({ minWidth: '240px' })}>
							<label className={css(labelStyle)}>Category</label>
							<Select
								options={categoryOptions}
								labelKey='label'
								valueKey='id'
								clearable
								searchable
								placeholder='All categories'
								value={categoryValue}
								onChange={({ value }) => setCategoryValue(value)}
							/>
						</div>
						<div className={css({ minWidth: '180px' })}>
							<label className={css(labelStyle)}>Severity</label>
							<Select
								options={SEVERITY_OPTIONS}
								labelKey='label'
								valueKey='id'
								clearable={false}
								searchable={false}
								value={severityValue}
								onChange={({ value }) => setSeverityValue(value)}
							/>
						</div>
						<div className={css({ minWidth: '200px' })}>
							<label className={css(labelStyle)}>Violations</label>
							<Select
								options={HAS_ACTION_OPTIONS}
								labelKey='label'
								valueKey='id'
								clearable={false}
								searchable={false}
								value={hasActionValue}
								onChange={({ value }) => setHasActionValue(value)}
							/>
						</div>
					</div>

					<div className={css({ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' })}>
						<div className={css({ width: '140px' })}>
							<label className={css(labelStyle)}>Page size</label>
							<Input value={pageSize} onChange={(e) => setPageSize(e.currentTarget.value)} type='number' />
						</div>
						<div>
							<Button onClick={handleSearch} isLoading={loading}>
								Search
							</Button>
						</div>
					</div>
				</div>
			</div>

			{notification && (
				<div className={css({ marginBottom: '16px' })}>
					<Notification kind={notification.kind} onClose={() => setNotification(null)}>
						{notification.message}
					</Notification>
				</div>
			)}

			{/* Results card */}
			{hasSearched && items.length > 0 && (
				<div className={css({ ...cardStyle, padding: 0, overflow: 'hidden' })}>
					<div className={css({ overflowX: 'auto', maxHeight: '60vh' })}>
						<table className={css({ width: '100%', borderCollapse: 'collapse' })}>
							<thead>
								<tr>
									<th className={css(headerCellStyle)}>User ID</th>
									<th className={css(headerCellStyle)}>Bot ID</th>
									<th className={css(headerCellStyle)}>Top Category</th>
									<th className={css(headerCellStyle)}>Severity Mix</th>
									<th className={css(headerCellStyle)}>Engine</th>
									<th className={css(headerCellStyle)}>Det</th>
									<th className={css(headerCellStyle)}>Act</th>
									<th className={css({ ...headerCellStyle, whiteSpace: 'normal', minWidth: '180px' })}>Status</th>
									<th className={css(headerCellStyle)}>Last seen</th>
								</tr>
							</thead>
							<tbody>
								{items.map((row) => {
									const canOpen = !!row.userId && !!row.botId;
									return (
										<tr
											key={`${row.userId}-${row.botId}`}
											onClick={() => {
												if (canOpen) setSelectedChat({ userId: row.userId, botId: row.botId });
											}}
											title={canOpen ? 'Click to review conversation' : 'Missing userId or botId'}
											className={css({
												cursor: canOpen ? 'pointer' : 'default',
												':hover': canOpen ? { backgroundColor: '#eef2ff' } : {}
											})}>
											<td className={css(cellStyle)}>{row.userId}</td>
											<td className={css({ ...cellStyle, fontFamily: 'monospace', fontSize: '12px' })}>
												{row.botId}
											</td>
											<td className={css(cellStyle)}>
												{row.topCategory ?? '—'}
												{row.categories && row.categories.length > 1 && (
													<span className={css({ color: '#9ca3af', fontSize: '11px', marginLeft: '6px' })}>
														+{row.categories.length - 1} more
													</span>
												)}
											</td>
											<td className={css(cellStyle)}>
												<SeverityMix counts={row.severityCounts} css={css} />
											</td>
											<td className={css(cellStyle)}>
												<EngineBadges engines={row.engines} css={css} />
											</td>
											<td className={css(cellStyle)}>
												{typeof row.pendingViolationCount === 'number' &&
												row.pendingViolationCount !== row.violationCount
													? `${row.pendingViolationCount} / ${row.violationCount}`
													: row.violationCount}
											</td>
											<td className={css(cellStyle)}>{row.actionCount}</td>
											<td className={css({ ...cellStyle, whiteSpace: 'normal', minWidth: '180px' })}>
												<StatusBadges item={row} css={css} />
											</td>
											<td className={css(cellStyle)}>{formatTimestamp(row.lastTimestamp)}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					<div
						className={css({
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							padding: '12px 16px',
							borderTop: '1px solid #e5e7eb',
							backgroundColor: '#f9fafb'
						})}>
						<span className={css({ fontSize: '13px', color: '#6b7280' })}>
							Page {page} of {totalPages} • {total} pair{total === 1 ? '' : 's'}
						</span>
						<div className={css({ display: 'flex', gap: '8px' })}>
							<Button size='compact' kind='secondary' disabled={!canPrev} onClick={() => runSearch(page - 1)}>
								Prev
							</Button>
							<Button size='compact' kind='secondary' disabled={!canNext} onClick={() => runSearch(page + 1)}>
								Next
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default GameplayGuardrailReviewPanel;
